import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ChevronDownIcon, 
  ChevronUpIcon, 
  FunnelIcon 
} from '@heroicons/react/24/outline';

export default function FilterPane() {
  const [isOpen, setIsOpen] = useState(false); // Collapsed by default on mobile
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // Controls multi-select drawer
  const [searchTerm, setSearchTerm] = useState(''); // Features instant lookup filter
  const [errorMessage, setErrorMessage] = useState(''); // Inline alert tracker
  
  const [filters, setFilters] = useState({
    type: '',
    make: '',
    model: '',
    condition: '',
    transmission: '',
    fuelType: '',
    driveType: '',
    minYear: '',
    maxYear: '',
    location: '',
    keyFeatures: '' // Backed by clean array-conversion parsing
  });
  
  const navigate = useNavigate();
  const location = useLocation();

  // 36 requested Key Features Options list
  const keyFeaturesOptions = [
    "360 Camera", "Airbag", "AM/FM Radio", "Air Conditioning", "Alloy Wheels", 
    "Android Auto", "Anti-Lock Brakes", "Armrests", "Blind Spot Monitors", "Bullbar", 
    "Carplay", "Bluetooth", "Cruise Control", "Cup Holders", "Electric Mirrors", 
    "Electric Windows", "Front Fog", "Leather Seats", "LED Headlights", "Parking Assist", 
    "Panoramic Roof", "Parking Sensors", "Roof Rack", "Sidesteps", "Sunroof", 
    "Spotlight", "Traction Control", "Winch", "Xenon Lights", "Heated Seats", 
    "Leather Upholstery", "Navigation System", "Keyless Entry", "Exclusive Sound System", 
    "New Tyres", "Voice Recognition"
  ];

  // Checks if at least one filter input has a non-empty value
  const isFilterActive = Object.entries(filters).some(([key, value]) => {
    if (key === 'keyFeatures') {
      return typeof value === 'string' && value.trim() !== '';
    }
    return typeof value === 'string' && value.trim() !== '';
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setErrorMessage('');
  };

  // Safe toggle feature handler for multi-select key features
  const toggleFeature = (feature) => {
    setFilters(prev => {
      const currentString = prev.keyFeatures || '';
      // Parse current string to array
      const currentList = currentString ? currentString.split(',').map(f => f.trim()) : [];
      
      const isSelected = currentList.includes(feature);
      const updatedList = isSelected
        ? currentList.filter(f => f !== feature)
        : [...currentList, feature];
        
      return {
        ...prev,
        keyFeatures: updatedList.join(', ') // Return as comma-separated list
      };
    });
    setErrorMessage('');
  };

  const applyFilters = () => {
    if (!isFilterActive) {
      setErrorMessage('Please select or input at least one filter parameter first.');
      return;
    }

    setErrorMessage('');
    const params = new URLSearchParams(location.search);
    
    // Clear old parameters to prevent stacking duplicates
    params.delete('body_type');
    params.delete('make');
    params.delete('model');
    params.delete('condition');
    params.delete('transmission');
    params.delete('fuel_type');
    params.delete('drive_train');
    params.delete('minYear');
    params.delete('maxYear');
    params.delete('location');
    params.delete('keyFeatures');

    // Set active filter parameters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        if (key === 'type') params.set('body_type', value);
        else if (key === 'fuelType') params.set('fuel_type', value);
        else if (key === 'driveType') params.set('drive_train', value);
        else params.set(key, value);
      }
    });
    
    navigate(`/browse?${params.toString()}`);
  };

  const selectedFeaturesArray = filters.keyFeatures 
    ? filters.keyFeatures.split(',').map(f => f.trim()).filter(Boolean) 
    : [];

  return (
    <div className="w-full select-none">
      
      {/* Mobile-Only Collapsible Toggle Header */}
      <button 
        onClick={() => setIsOpen(!isOpen)} 
        className="lg:hidden w-full flex items-center justify-between p-3.5 bg-blue-900 text-white rounded-xl shadow-sm focus:outline-none transition-all duration-300"
      >
        <div className="flex items-center space-x-2">
          <FunnelIcon className="h-5 w-5" />
          <span className="font-bold text-sm tracking-wider uppercase font-sans">FILTERS</span>
        </div>
        {isOpen ? (
          <ChevronUpIcon className="h-5 w-5" />
        ) : (
          <ChevronDownIcon className="h-5 w-5" />
        )}
      </button>

      {/* Main Filter Content Container */}
      <div className={`mt-3 lg:mt-0 ${isOpen ? 'block' : 'hidden lg:block'} bg-white p-6 rounded-2xl border border-gray-200/80 lg:border-gray-100 shadow-md`}>
        <h2 className="text-lg font-bold text-blue-950 font-sans tracking-wide mb-6 border-b border-gray-100 pb-2 hidden lg:block">
          FILTERS
        </h2>
        
        {/* Form elements in 2 columns on mobile, stacked in 1 column on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Type (Body Type)</label>
            <select name="type" value={filters.type} onChange={handleFilterChange} className="w-full p-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-900 focus:border-blue-900 outline-none transition text-black">
              <option value="">All</option>
              <option value="Sedan">Sedan</option>
              <option value="SUV">SUV</option>
              <option value="Truck">Truck</option>
              <option value="Van">Van</option>
              <option value="Coupe">Coupe</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Make</label>
            <input 
              type="text" 
              name="make" 
              value={filters.make} 
              onChange={handleFilterChange} 
              placeholder="e.g. Toyota" 
              className="w-full p-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-900 focus:border-blue-900 outline-none transition text-black placeholder-gray-400" 
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Model</label>
            <input 
              type="text" 
              name="model" 
              value={filters.model} 
              onChange={handleFilterChange} 
              placeholder="e.g. Camry" 
              className="w-full p-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-900 focus:border-blue-900 outline-none transition text-black placeholder-gray-400" 
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Condition</label>
            <select 
              name="condition" 
              value={filters.condition} 
              onChange={handleFilterChange} 
              className="w-full p-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-900 focus:border-blue-900 outline-none transition text-black"
            >
              <option value="">All</option>
              <option value="New">New</option>
              <option value="Foreign Used">Foreign Used</option>
              <option value="Nigerian Used">Nigerian Used</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Transmission</label>
            <select 
              name="transmission" 
              value={filters.transmission} 
              onChange={handleFilterChange} 
              className="w-full p-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-900 focus:border-blue-900 outline-none transition text-black"
            >
              <option value="">All</option>
              <option value="Automatic">Automatic</option>
              <option value="Manual">Manual</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Fuel Type</label>
            <select 
              name="fuelType" 
              value={filters.fuelType} 
              onChange={handleFilterChange} 
              className="w-full p-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-900 focus:border-blue-900 outline-none transition text-black"
            >
              <option value="">All</option>
              <option value="Petrol">Petrol</option>
              <option value="Diesel">Diesel</option>
              <option value="Hybrid">Hybrid</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Drive Type</label>
            <select 
              name="driveType" 
              value={filters.driveType} 
              onChange={handleFilterChange} 
              className="w-full p-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-900 focus:border-blue-900 outline-none transition text-black"
            >
              <option value="">All</option>
              <option value="FWD">FWD</option>
              <option value="RWD">RWD</option>
              <option value="AWD">AWD</option>
              <option value="4WD">4WD</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Min Year</label>
            <input 
              type="number" 
              name="minYear" 
              value={filters.minYear} 
              onChange={handleFilterChange} 
              placeholder="e.g. 2015" 
              className="w-full p-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-900 focus:border-blue-900 outline-none transition text-black placeholder-gray-400" 
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Max Year</label>
            <input 
              type="number" 
              name="maxYear" 
              value={filters.maxYear} 
              onChange={handleFilterChange} 
              placeholder="e.g. 2026" 
              className="w-full p-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-900 focus:border-blue-900 outline-none transition text-black placeholder-gray-400" 
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Location</label>
            <input 
              type="text" 
              name="location" 
              value={filters.location} 
              onChange={handleFilterChange} 
              placeholder="e.g. Lagos" 
              className="w-full p-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-900 focus:border-blue-900 outline-none transition text-black placeholder-gray-400" 
            />
          </div>

          {/* Upgraded Multi-Select Dropdown Container with Search Input inside */}
          <div className="relative col-span-2 lg:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Key Features
            </label>
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full p-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-900 focus:border-blue-900 outline-none transition text-black flex justify-between items-center text-left"
            >
              <span className="truncate max-w-[85%] text-gray-800">
                {selectedFeaturesArray.length > 0
                  ? selectedFeaturesArray.join(', ')
                  : "Select Key Features"}
              </span>
              <ChevronDownIcon className="h-4 w-4 text-gray-500 ml-2 shrink-0" />
            </button>
            
            {isDropdownOpen && (
              <>
                {/* Backdrop catcher layer to securely support tap outside drawer closure */}
                <div 
                  className="fixed inset-0 z-10 cursor-default" 
                  onClick={() => setIsDropdownOpen(false)}
                />
                
                {/* Dropdown Card */}
                <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 p-3 max-h-64 overflow-y-auto space-y-3">
                  
                  {/* Dynamic Inline Search Field */}
                  <input
                    type="text"
                    placeholder="Search features..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-2 text-xs border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-900 focus:border-blue-900 outline-none text-black bg-gray-50"
                  />

                  {/* List items scroll container */}
                  <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                    {keyFeaturesOptions
                      .filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase()))
                      .map(opt => {
                        const isChecked = selectedFeaturesArray.includes(opt);
                        return (
                          <label 
                            key={opt} 
                            className="flex items-center space-x-2.5 p-1.5 hover:bg-gray-50 rounded cursor-pointer transition text-xs select-none"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleFeature(opt)}
                              className="h-3.5 w-3.5 text-blue-900 focus:ring-blue-900 border-gray-300 rounded cursor-pointer"
                            />
                            <span className={`${isChecked ? 'font-semibold text-blue-900' : 'text-gray-700'}`}>
                              {opt}
                            </span>
                          </label>
                        );
                      })}
                    {keyFeaturesOptions.filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 && (
                      <p className="text-xs text-gray-400 text-center py-4 font-light">No features match your query</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Dynamic Apply Filter Action button */}
        <div className="mt-6 col-span-2 lg:col-span-1">
          <button 
            onClick={applyFilters} 
            className={`w-full font-bold p-3 rounded-lg shadow-sm transition duration-300 ${
              isFilterActive 
                ? 'bg-blue-900 hover:bg-blue-800 text-white cursor-pointer hover:shadow-md' 
                : 'bg-gray-200 text-gray-400 border border-gray-200 cursor-not-allowed'
            }`}
          >
            APPLY FILTERS
          </button>
          
          {/* Validation Alert Notice */}
          {errorMessage && (
            <p className="mt-3 text-xs font-medium text-red-600 bg-red-50 p-2.5 rounded-lg border border-red-100 text-justify transition-all duration-300 animate-fadeIn">
              ⚠️ {errorMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
